import React, { useState, useEffect, useRef } from 'react';
import BarcodeScanner from '../components/BarcodeScanner';
import { createProduct, getProductByBarcode, getVendors, createVendor } from '../api';

function Buying({ user }) {
  const [showScanner, setShowScanner] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [showAddVendor, setShowAddVendor] = useState(false);
  const [newVendorName, setNewVendorName] = useState('');
  const [formData, setFormData] = useState({
    barcode: '',
    name: '',
    description: '',
    variation: '',
    vendor: 'Hobby Lobby',
    cost: '',
    store_price: '',
    quantity: '1',
  });
  const [photos, setPhotos] = useState([]);
  const [draggedPhotoIndex, setDraggedPhotoIndex] = useState(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const nameInputRef = useRef(null);

  useEffect(() => {
    loadVendors();
  }, []);

  const loadVendors = async () => {
    try {
      const response = await getVendors();
      setVendors(response.data);
    } catch (err) {
      console.error('Failed to load vendors');
    }
  };

  const handleAddVendor = async () => {
    if (!newVendorName.trim()) return;

    try {
      const response = await createVendor(newVendorName.trim());
      setVendors([...vendors, response.data]);
      setFormData({ ...formData, vendor: response.data.name });
      setNewVendorName('');
      setShowAddVendor(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add vendor');
    }
  };

  const handleScan = async (barcode) => {
    setShowScanner(false);
    setFormData({ ...formData, barcode });

    // Check if product exists
    try {
      const response = await getProductByBarcode(barcode);
      if (response.data) {
        setMessage('Product already exists in database');
        setFormData({
          barcode,
          name: response.data.name,
          description: response.data.description || '',
          variation: response.data.variation || '',
          vendor: response.data.vendor || 'Hobby Lobby',
          cost: response.data.cost || '',
          store_price: response.data.store_price || '',
          quantity: '1',
        });
      }
    } catch (err) {
      // Product doesn't exist, continue with form
    }

    // Focus on name input after scan to help iOS recover input focus
    setTimeout(() => {
      if (nameInputRef.current) {
        nameInputRef.current.focus();
        // Immediately blur to reset iOS keyboard state without showing keyboard
        nameInputRef.current.blur();
      }
    }, 150);
  };

  const handlePhotosChange = (e) => {
    const files = Array.from(e.target.files);
    const totalPhotos = photos.length + files.length;

    if (totalPhotos > 5) {
      setError(`Maximum 5 photos allowed. You can add ${5 - photos.length} more.`);
      return;
    }

    const newPhotoObjects = files.map(file => ({
      file,
      preview: URL.createObjectURL(file)
    }));

    setPhotos([...photos, ...newPhotoObjects]);
    e.target.value = '';
  };

  const handleRemovePhoto = (index) => {
    const updated = [...photos];
    URL.revokeObjectURL(updated[index].preview);
    updated.splice(index, 1);
    setPhotos(updated);
  };

  // Drag and drop handlers for reordering
  const handleDragStart = (index) => {
    setDraggedPhotoIndex(index);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (targetIndex) => {
    if (draggedPhotoIndex === null || draggedPhotoIndex === targetIndex) {
      setDraggedPhotoIndex(null);
      return;
    }

    const updated = [...photos];
    const [moved] = updated.splice(draggedPhotoIndex, 1);
    updated.splice(targetIndex, 0, moved);
    setPhotos(updated);
    setDraggedPhotoIndex(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    const data = new FormData();
    data.append('barcode', formData.barcode);
    data.append('name', formData.name);
    data.append('description', formData.description);
    data.append('variation', formData.variation);
    data.append('vendor', formData.vendor);
    data.append('cost', formData.cost);
    data.append('store_price', formData.store_price);
    data.append('quantity', formData.quantity);

    // Add all photos
    photos.forEach(photo => {
      data.append('photos', photo.file);
    });

    try {
      await createProduct(data);
      setMessage('Product added successfully!');
      setFormData({
        barcode: '',
        name: '',
        description: '',
        variation: '',
        vendor: 'Hobby Lobby',
        cost: '',
        store_price: '',
        quantity: '1',
      });
      // Clean up photo previews
      photos.forEach(photo => URL.revokeObjectURL(photo.preview));
      setPhotos([]);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add product');
    }
  };

  if (user.role !== 'admin') {
    return (
      <div className="container">
        <div className="alert alert-error">
          Only administrators can add products.
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="card">
        <div className="card-header">Add New Product</div>

        {message && <div className="alert alert-success">{message}</div>}
        {error && <div className="alert alert-error">{error}</div>}

        {showScanner ? (
          <BarcodeScanner
            onScan={handleScan}
            onClose={() => setShowScanner(false)}
          />
        ) : (
          <>
            <button
              className="btn btn-primary btn-full"
              onClick={() => setShowScanner(true)}
              style={{ marginBottom: '20px' }}
            >
              Scan Barcode
            </button>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Barcode</label>
                <input
                  type="text"
                  value={formData.barcode}
                  onChange={(e) =>
                    setFormData({ ...formData, barcode: e.target.value })
                  }
                  placeholder="Scan or enter manually"
                />
              </div>

              <div className="form-group">
                <label>Product Name *</label>
                <input
                  ref={nameInputRef}
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  required
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                />
              </div>

              <div className="form-group">
                <label>Variation</label>
                <input
                  type="text"
                  value={formData.variation}
                  onChange={(e) =>
                    setFormData({ ...formData, variation: e.target.value })
                  }
                  placeholder="e.g., Size, Color"
                />
              </div>

              <div className="form-group">
                <label>Vendor</label>
                {!showAddVendor ? (
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <select
                      value={formData.vendor}
                      onChange={(e) =>
                        setFormData({ ...formData, vendor: e.target.value })
                      }
                      style={{ flex: 1 }}
                    >
                      {vendors.map((vendor) => (
                        <option key={vendor.id} value={vendor.name}>
                          {vendor.name}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setShowAddVendor(true)}
                      style={{ padding: '8px 12px' }}
                    >
                      + New
                    </button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <input
                      type="text"
                      value={newVendorName}
                      onChange={(e) => setNewVendorName(e.target.value)}
                      placeholder="Enter new vendor name"
                      style={{ flex: 1 }}
                    />
                    <button
                      type="button"
                      className="btn btn-success"
                      onClick={handleAddVendor}
                      style={{ padding: '8px 12px' }}
                    >
                      Add
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => {
                        setShowAddVendor(false);
                        setNewVendorName('');
                      }}
                      style={{ padding: '8px 12px' }}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>Store Price *</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.store_price}
                  onChange={(e) =>
                    setFormData({ ...formData, store_price: e.target.value })
                  }
                  required
                />
              </div>

              <div className="form-group">
                <label>Cost (USD) *</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.cost}
                  onChange={(e) =>
                    setFormData({ ...formData, cost: e.target.value })
                  }
                  required
                />
              </div>

              <div className="form-group">
                <label>Quantity *</label>
                <input
                  type="number"
                  value={formData.quantity}
                  onChange={(e) =>
                    setFormData({ ...formData, quantity: e.target.value })
                  }
                  required
                />
              </div>

              <div className="form-group">
                <label>Product Photos ({photos.length}/5)</label>

                {/* Photo Grid */}
                {photos.length > 0 && (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                    gap: '10px',
                    marginBottom: '10px'
                  }}>
                    {photos.map((photo, index) => (
                      <div
                        key={index}
                        draggable
                        onDragStart={() => handleDragStart(index)}
                        onDragOver={handleDragOver}
                        onDrop={() => handleDrop(index)}
                        style={{
                          position: 'relative',
                          aspectRatio: '1',
                          cursor: 'grab',
                          border: index === 0 ? '2px solid var(--color-primary)' : 'none',
                          borderRadius: '8px'
                        }}
                      >
                        <img
                          src={photo.preview}
                          alt={`Photo ${index + 1}`}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            borderRadius: '8px'
                          }}
                        />
                        {index === 0 && (
                          <span style={{
                            position: 'absolute',
                            top: '4px',
                            left: '4px',
                            backgroundColor: 'var(--color-primary)',
                            color: 'white',
                            fontSize: '0.65rem',
                            padding: '2px 6px',
                            borderRadius: '4px'
                          }}>
                            Primary
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={() => handleRemovePhoto(index)}
                          style={{
                            position: 'absolute',
                            top: '-8px',
                            right: '-8px',
                            background: 'var(--color-error)',
                            border: '2px solid white',
                            color: 'white',
                            borderRadius: '50%',
                            width: '32px',
                            height: '32px',
                            cursor: 'pointer',
                            fontSize: '1.1rem',
                            fontWeight: 'bold',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add Photos Button */}
                {photos.length < 5 && (
                  <div>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handlePhotosChange}
                      id="photo-upload"
                      style={{ display: 'none' }}
                    />
                    <label
                      htmlFor="photo-upload"
                      className="btn btn-secondary"
                      style={{ cursor: 'pointer', display: 'inline-block' }}
                    >
                      + Add Photos
                    </label>
                    {photos.length > 0 && (
                      <span style={{ marginLeft: '10px', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>
                        Drag to reorder. First photo is primary.
                      </span>
                    )}
                  </div>
                )}
              </div>

              <button type="submit" className="btn btn-success btn-full">
                Add Product
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

export default Buying;
